import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';


import {clearSelection} from 'src/components/data-table-selection';
import {GroupSuggest} from 'src/components/group-suggest';
import {FilterInput} from 'src/components/input';
import {ACCOUNT_TABS} from 'src/constants';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {UsersGroupsHeader} from 'src/modules/profile/account/tabs/users-and-groups/users-groups-header';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {useGateValue} from '@pb/kit-lib-statsig';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

import {SELECTION_KEY} from '../../users-groups-search-result/constants';
import {userSearchResultStateActions, userSearchResultStateSelector} from '../../users-groups-search-result/slices/user-search-result-state.slice';

import {UsersFilter} from './filter/users-filter';


import type {AccountGroup} from 'src/openapi/admin/models/AccountGroup';

export const Header = ({refreshVersion = 0}: {refreshVersion?: number}) => {
    const provisioningEnabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const queryParams = useParams<{accountId: string, userType?: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const userType = queryParams.userType;
    const isConferenceAccount = useConferenceAccountCheck();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const cacheKeys = useSelector(userSearchResultStateSelector);

    const {canCreateUser, canBulkImportUser} = useAdminAccessRight();
    const {userCreateOpen} = useEventTracking();

    const changeEmailFilter = useCallbackWithRef((email: string) => {
        if (provisioningEnabled) {
            dispatch(clearSelection(SELECTION_KEY));
        }
        const {userEmails: _userEmails, ...other} = cacheKeys.searchCriteria;

        if (email) {
            dispatch(userSearchResultStateActions.setSearchCriteria({
                userEmails: [email],
                ...other,
            }));
        } else {
            dispatch(userSearchResultStateActions.setSearchCriteria(other));
        }
    });

    const clearEmailFilter = useCallback(() => {
        if (provisioningEnabled) {
            dispatch(clearSelection(SELECTION_KEY));
        }
        const {userEmails: _userEmails, ...other} = cacheKeys.searchCriteria;

        dispatch(userSearchResultStateActions.setSearchCriteria(other));
    }, [cacheKeys, dispatch, provisioningEnabled]);

    const changeGroupFilter = useCallback((selectedGroup: AccountGroup | null) => {
        const {groupId: _groupId, ...other} = cacheKeys.searchCriteria;

        if (selectedGroup) {
            dispatch(userSearchResultStateActions.setSearchCriteria({
                groupId: selectedGroup.id.toString(),
                ...other,
            }));
        } else {
            dispatch(userSearchResultStateActions.setSearchCriteria(other));
        }
    }, [cacheKeys, dispatch]);

    const emailFilter = (
        <FilterInput
            id="email-filter-input"
            width={300}
            value={(cacheKeys.searchCriteria.userEmails as string[] | undefined)?.find(Boolean) || ''}
            placeholder="Search for users by email"
            maxLength={60}
            onChange={changeEmailFilter}
            onClear={clearEmailFilter}
            timeOut={1500}
        />
    );
    const actions = (
        <Group>
            {(canBulkImportUser(accountId) && !isConferenceAccount) ? (
                <Button
                    variant="Type.SECONDARY"
                    onClick={() => navigate(`/create-new-user/${accountId}`, {state: {prevPath: pathname}})}>
                    Bulk Import
                </Button>
            ) : null}
            {(canCreateUser(accountId) && !isConferenceAccount) ? (
                <Button
                    variant="Type.PRIMARY"
                    onClick={() => {
                        userCreateOpen({place: 'User & Groups'});
                        navigate(`/profile/account/${accountId}/${ACCOUNT_TABS.CREATE_USER.name}`);
                    }}>
                    Add User
                </Button>
            ) : null}
        </Group>
    );

    return (
        <UsersGroupsHeader activeTab={userType || 'all'}>
            {provisioningEnabled ? (
                <UsersFilter key={accountId} accountId={accountId} refreshVersion={refreshVersion} actions={actions}>
                    {emailFilter}
                </UsersFilter>
            ) : (
                <Group justify="space-between">
                    <Group>
                        <GroupSuggest {...{
                            id: 'group-filter-suggest',
                            placeholder: 'Select target group',
                            selectedGroupId: cacheKeys.searchCriteria.groupId
                                ? +cacheKeys.searchCriteria.groupId : null,
                            width: 320,
                            onSelect: changeGroupFilter,
                            accountId,
                        }}
                        />
                        {emailFilter}
                    </Group>
                    {actions}
                </Group>
            )}
        </UsersGroupsHeader>
    );
};
